import React from 'react';
import { Search, Calendar, Users, Tag, LayoutList, Kanban, Plus } from 'lucide-react';
import { User } from '../../types';
interface NotesFiltersProps {
  onSearch: (query: string) => void;
  onUserFilter: (userId: string) => void;
  onDateFilter: (range: string) => void;
  onCategoryFilter: (category: string) => void;
  viewMode: 'list' | 'board';
  onViewChange: (mode: 'list' | 'board') => void;
  users: User[];
  selectedUser: string;
  selectedCategory: string;
  selectedDateRange: string;
  onNewNote: () => void;
}
const CATEGORIES = ['General', 'Underwriting', 'Processing', 'Closing', 'Call', 'Email'];
export function NotesFilters({
  onSearch,
  onUserFilter,
  onDateFilter,
  onCategoryFilter,
  viewMode,
  onViewChange,
  users,
  selectedUser,
  selectedCategory,
  selectedDateRange,
  onNewNote
}: NotesFiltersProps) {
  return <div className="flex flex-nowrap gap-2.5 mb-5 items-center bg-white px-4 py-3 rounded-xl border border-slate-200 shadow-sm" data-id="element-213">
      {/* Search */}
      <div className="relative w-48 shrink-0" data-id="element-214">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" data-id="element-215" />
        <input type="text" placeholder="Search notes..." className="w-full pl-9 pr-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white placeholder:text-slate-400 text-slate-700 transition-colors" onChange={e => onSearch(e.target.value)} data-id="element-216" />
      </div>

      <div className="h-6 w-px bg-slate-200 shrink-0" data-id="element-217" />

      {/* Author filter */}
      <div className="relative shrink-0" data-id="element-218">
        <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" data-id="element-219" />
        <select value={selectedUser} onChange={e => onUserFilter(e.target.value)} className={`appearance-none border text-sm rounded-lg pl-9 pr-8 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium cursor-pointer transition-colors ${selectedUser !== 'all' ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'}`} data-id="element-220">
          <option value="all" data-id="element-221">All Authors</option>
          {users.map(u => <option key={u.id} value={u.id} data-id="element-222">
              {u.name}
            </option>)}
        </select>
      </div>

      {/* Category filter */}
      <div className="relative shrink-0" data-id="element-223">
        <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" data-id="element-224" />
        <select value={selectedCategory} onChange={e => onCategoryFilter(e.target.value)} className={`appearance-none border text-sm rounded-lg pl-9 pr-8 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium cursor-pointer transition-colors ${selectedCategory !== 'all' ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'}`} data-id="element-225">
          <option value="all" data-id="element-226">All Categories</option>
          {CATEGORIES.map(c => <option key={c} value={c} data-id="element-227">
              {c}
            </option>)}
        </select>
      </div>

      {/* Date filter */}
      <div className="relative shrink-0" data-id="element-228">
        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" data-id="element-229" />
        <select value={selectedDateRange} onChange={e => onDateFilter(e.target.value)} className={`appearance-none border text-sm rounded-lg pl-9 pr-8 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium cursor-pointer transition-colors ${selectedDateRange !== 'all' ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'}`} data-id="element-230">
          <option value="all" data-id="element-231">All Time</option>
          <option value="today" data-id="element-232">Today</option>
          <option value="week" data-id="element-233">This Week</option>
          <option value="month" data-id="element-234">This Month</option>
          <option value="90days" data-id="element-235">Last 90 Days</option>
        </select>
      </div>

      {/* Spacer */}
      <div className="flex-1" data-id="element-236" />

      <div className="h-6 w-px bg-slate-200 shrink-0" data-id="element-237" />

      {/* View toggle */}
      <div className="flex bg-slate-100 p-0.5 rounded-lg border border-slate-200 shrink-0" data-id="element-238">
        <button onClick={() => onViewChange('list')} className={`p-2 rounded-md transition-all ${viewMode === 'list' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-400 hover:text-slate-600'}`} title="Timeline View" data-id="element-239">
          <LayoutList className="h-4 w-4" data-id="element-240" />
        </button>
        <button onClick={() => onViewChange('board')} className={`p-2 rounded-md transition-all ${viewMode === 'board' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-400 hover:text-slate-600'}`} title="Board View" data-id="element-241">
          <Kanban className="h-4 w-4" data-id="element-242" />
        </button>
      </div>

      <div className="h-6 w-px bg-slate-200 shrink-0" data-id="element-243" />

      {/* New Note button */}
      <button onClick={onNewNote} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shrink-0" data-id="element-244">
        <Plus className="h-4 w-4" data-id="element-245" />
        New Note
      </button>
    </div>;
}